//import { Modal } from './modal';
import { _js } from '@ifixit/localize';
import ValidationFunctions from 'Shared/validation';
import { CookieUtils } from 'Shared/cookies/cookie-utils';
import trackInMatomoAndGA from 'Shared/Analytics/CombinedGAMatomo';

// GLOBAL CONVENIENCE FUNCTIONS ///////////////////////////////////////////////

/**
 * Convenience function to call Element:getElement with the document as the
 * context object.
 */
export { $E };
window.$E = $E;
function $E(selector) {
   return document.getElement(selector);
}

/**
 * Return an object with the current subdomain and domain values.
 */
export { parseDomain };
window.parseDomain = parseDomain;
function parseDomain() {
   let matches = window.location.host.match(/\b([^.]+)\.([^.]+\.[^.]+)$/);
   return {
      subdomain: matches[1],
      domain: matches[2],
   };
}

/**
 * Parses URL for a specific parameter.
 */
export { getURLParameter };
window.getURLParameter = getURLParameter;
function getURLParameter(parameter) {
   return new URI(window.location.href).getData(parameter);
}

/**
 * As close as we can get to a new keyword, `when` makes sure that its first
 * argument is non-empty (or non-false), and then executes its second argument,
 * passing in the first.
 */
export { when };
window.when = when;
function when(obj, fn, fallback) {
   if (!obj || obj.length === 0) {
      return fallback;
   }

   return fn(obj);
}

/**
 * Registers a 'click' event handler on the element with the specified id,
 * if it exists in the DOM.
 */
export { clickSafe };
window.clickSafe = clickSafe;
function clickSafe(id, eventHandler) {
   return when($(id), function (element) {
      element.addEvent('click', eventHandler);
   });
}

/**
 * Ensures the handler is called when the window load event fires, or
 * immediately if the load event has already fired.
 */
export { onLoad };
window.onLoad = onLoad;
function onLoad(handler) {
   if (document.readyState == 'complete') {
      handler();
   } else {
      window.addEvent('load', handler);
   }
}

/**
 * Call handler before the window unloads, returning its return value to the
 * native event subscription.
 */
export { onUnload };
window.onUnload = onUnload;
function onUnload(handler) {
   window.addEventListener('beforeunload', function (e) {
      let msg = handler();
      if (msg) {
         // e.returnValue is needed for a few popular browsers
         return (e.returnValue = msg);
      }
   });
}

/**
 * Syntastic sugar around adding a domready even handler since we do it often.
 */
export { onDomReady };
window.onDomReady = onDomReady;
function onDomReady(handler) {
   document.addEvent('domready' /* @suppress domready */, function () {
      setTimeout(handler, 0);
   });
}

/**
 * If a url has a hash referring to a specific element, handler is called
 * with that element when the dom is ready.
 */
export { onHashFragmentElement };
window.onHashFragmentElement = onHashFragmentElement;
function onHashFragmentElement(handler) {
   let callHandlerWithElement = function callHandlerWithElement() {
      if (window.location.hash) {
         // Strip the # from the beginning of the hash
         let id = window.location.hash.substr(1);
         let element = $(id);

         if (element) {
            handler(element);
         }
      }
   };

   onDomReady(callHandlerWithElement);
   window.addEventListener('hashchange', callHandlerWithElement);
}

/**
 * Fire an event globally so that listeners can be setup before any class
 * is defined.
 */
export { fireGlobal };
window.fireGlobal = fireGlobal;
function fireGlobal(eventName) {
   $(window).fireEvent(eventName, Array.convert(arguments).slice(1));
}

/**
 * Listen to an event globally so that listeners can be setup before any class
 * is defined.
 */
export { listenGlobal };
window.listenGlobal = listenGlobal;
function listenGlobal(eventName, handler) {
   $(window).addEvent(eventName, handler);
}

/**
 * A javascript equivalent of the html() php function we have. To be used with
 * JS handlebar templates.
 */
export { html };
window.html = html;
function html(str) {
   return new Handlebars.SafeString(str);
}

/**
 * A debug helper that wraps every method of an element with a log message
 * that indicates that that method was called. Useful for seeing what sequence
 * of calls are being made on an element from various places. The optional
 * logger argument takes the element, method name, and method arguments as
 * parameters and can do whatever it wants with them.
 */
export { watch };
window.watch = watch;
function watch(el, logger) {
   if (!logger) {
      logger = function (el, prop, args) {
         // eslint-disable-next-line no-console
         console.log(prop); // @suppress console me
      };
   }
   for (let prop in el) {
      if (typeof el[prop] == 'function') {
         (function (prop, func) {
            el[prop] = function () {
               logger(el, prop, arguments);
               return func.apply(el, arguments);
            };
         })(prop, el[prop]);
      }
   }
}

/**
 * An error class to distinguish from a generic Error, which is usually caused
 * by programmer error. The distinction can be made with the 'instanceof'
 * operator:
 *
 *    try {
 *       // something risky
 *    } catch (err) {
 *       if (err instanceof ApplicationError)
 *          // handle it
 *       else
 *          throw err
 *    }
 */
export { ApplicationError };
window.ApplicationError = ApplicationError;
function ApplicationError(msg) {
   this.message = msg;
}

/**
 * Returns true if the user agent matches that of a known mobile device, and
 * false otherwise.
 * Note: This regex is duplicated in Utils.php
 * Note: this is from: http://stackoverflow.com/a/11381730/13216 which is in
 * turn from http://detectmobilebrowsers.com/
 */
// eslint-disable-next-line no-var
export var mobileDetected = (window.mobileDetected = (function () {
   let isMobile = null;
   return function () {
      if (isMobile !== null) {
         return isMobile;
      }
      let UA = navigator.userAgent || navigator.vendor || window.opera,
         smallUA = UA.substr(0, 4),
         UAMatcher =
            /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i,
         smallUAMatcher =
            /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i;
      return (isMobile = UAMatcher.test(UA) || smallUAMatcher.test(smallUA));
   };
})());

// eslint-disable-next-line no-var
export var tabletDetected = (window.tabletDetected = (function () {
   let isTablet = null;
   return function () {
      if (isTablet !== null) {
         return isTablet;
      }
      let UA = navigator.userAgent || navigator.vendor || window.opera,
         UAMatcher = /android|ipad|playbook|silk/i;
      return (isTablet = UAMatcher.test(UA));
   };
})());

// eslint-disable-next-line no-var
export var androidDetected = (window.androidDetected = (function () {
   let isMobile = null;
   return function () {
      if (isMobile !== null) {
         return isMobile;
      }
      let UA = navigator.userAgent || navigator.vendor || window.opera;
      isMobile = /android/i.test(UA);
      return isMobile;
   };
})());

// FUNCTION EXTENSIONS ////////////////////////////////////////////////////////

Function.implement({
   /**
    * Extend functions with 'wrap', which lets us wrap a function g around a
    * function f, giving g full control over whether or not f gets called, and
    * with what arguments:
    *
    *    function f(msg) {alert(msg)};
    *    var g = f.wrap(function(h, msg) {h('From g: ' + msg)});
    *    g('Hello, World!') => "From g: Hello, World!"
    */
   wrap: function (wrapper) {
      let __method = this;
      return function () {
         return wrapper.apply(this, [__method.bind(this)].concat(Array.convert(arguments)));
      };
   },
});

// ELEMENT EXTENSIONS /////////////////////////////////////////////////////////

/**
 * Add an event that fires once and then removes itself.
 */
[Element, Window, Document, Events].invoke('implement', {
   addOneTimeEvent: function (type, fn) {
      // eslint-disable-next-line no-var
      var callback = function () {
         this.removeEvent(type, callback);
         return fn.apply(this, arguments);
      };
      return this.addEvent(type, callback);
   },
});

/**
 * Extend the Element class with our own custom methods.
 */
Element.implement({
   /**
    * Centers the element on the page, based on the viewport and element
    * dimensions as gleaned from Window.getSize() and Element.getSize(),
    * respectively.
    */
   center: function (options) {
      let el = this.getDimensions();
      let win = window.getSize();
      let scroll = options && options.position == 'top' ? { x: 0, y: 0 } : window.getScroll();

      let left = Math.round(scroll.x + (win.x - el.x) / 2);
      let top = Math.round(scroll.y + (win.y - el.y) / 2);

      this.setStyles({
         left: (left < 0 ? 5 : left) + 'px',
         top: (top < 0 ? 5 : top) + 'px',
      });

      return this;
   },

   /**
    * Adds the 'disabled' attribute. If the element is a button (has the
    * buttonLink class) then add our disabled button style.
    */
   disable: function () {
      this.set('disabled', true);

      if (this.hasClass('buttonLink')) {
         this.addClass('buttonLinkDisabled');
      }
      return this;
   },

   /**
    * Removes the 'disabled' attribute if it exists.
    * If the element is a button and has our buttonLinkDisabled style, remove
    * it.
    */
   enable: function () {
      this.removeAttribute('disabled');

      if (this.hasClass('buttonLinkDisabled')) {
         this.removeClass('buttonLinkDisabled');
      }
      return this;
   },

   /**
    * Sets the element's visibility property to the default value, making the
    * element visible. Returns the element for chaining.
    */
   makeVisible: function () {
      this.setStyle('visibility', '');
      return this;
   },

   /**
    * Sets the element's visibility property to 'hidden', making the element
    * invisible, but keeping it in the page flow. Returns the element for
    * chaining.
    */
   makeInvisible: function () {
      this.setStyle('visibility', 'hidden');
      return this;
   },

   /**
    * Returns true if the element is visible on the page, false otherwise.
    * Taken from http://stackoverflow.com/questions/1056357 (best way to find
    * if a dom object is visible or not using mootools) and modified very
    * slightly to negate the logic.
    */
   visible: function () {
      let w = this.offsetWidth,
         h = this.offsetHeight;
      let force = this.tagName === 'TR';
      let hidden =
         w === 0 && h === 0 && !force
            ? true
            : w !== 0 && h !== 0 && !force
            ? false
            : this.getStyle('display') === 'none';
      return !hidden;
   },

   fadeAndDispose: function (options) {
      let el = this;
      if (options === undefined) {
         options = {};
      }
      this.get('tween').chain(el.dispose.bind(el));
      this.set('tween', options).fade('out');
   },

   /**
    * Alias for Element.set('html').
    */
   update: function (str) {
      return this.set('html', str);
   },

   /**
    * Sets or toggles the visibility of this element.
    * Calls show() if visibility is true, hide() if it's false
    * and toggles the element if the argument is left out
    */
   toggle: function (visibility) {
      let show = arguments.length > 0 ? visibility : !this.visible();
      return show ? this.show() : this.hide();
   },

   /**
    * Gives focus to and tries to select a form field.
    */
   activate: function () {
      let tag = this.get('tag');
      let type = this.get('type');

      try {
         this.focus();
         if (
            this.select != undefined &&
            (tag == 'textarea' || (tag == 'input' && ['text', 'password'].contains(type)))
         ) {
            this.select();
         }
         // eslint-disable-next-line no-empty
      } catch (e) {}

      return this;
   },

   /**
    * Returns true if the element is 100% within the bounding rectangle of the
    * browser window i.e. the document doesn't need to be scrolled for it to be
    * visible.
    *
    * Note: doesn't take into account css visibility nor nested scrolling with
    * overflow: hidden.
    *
    * Taken from here: http://stackoverflow.com/a/7557433
    */
   isOnScreen: function () {
      let rect = this.getBoundingClientRect();

      return (
         rect.top >= 0 &&
         rect.left >= 0 &&
         rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
         rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
   },

   /**
    * Tries to scroll to the element smoothly.
    */
   jumpTo: function (options) {
      options = options || {};
      let offset = options.offset !== undefined ? options.offset : 100;
      let duration = options.duration !== undefined ? options.duration : 0.3;
      let callback = options.onComplete || function () {};

      return TweenMax.to(window, duration, {
         scrollTo: { y: this.getPosition().y - offset },
         onComplete: callback,
      });
   },

   /**
    * This will add a smooth scrolling animation when clicking a link to
    * an anchor (e.g., <a href="#section-1">). It overrides the default browser
    * behavior that instantly jumps to the anchor.
    *
    * Affects all <a href="#..."> children of this Element.
    */
   animateLinkedHashChanges: function (duration, afterHashChange) {
      this.addEvent('click:relay(a[href^=#])', function (ev) {
         // ev.target may be the <a> element or an element within the <a>
         let a = ev.target.tagName.toLowerCase() == 'a' ? ev.target : ev.target.getParent('a');

         let hash = a.get('href');
         let id = hash ? hash.substr(1) : null; // strip the leading #
         let el = id ? $(id) : null;

         let onComplete = function () {
            if (afterHashChange) {
               afterHashChange(el);
            }
         };

         if (!el) {
            return;
         }

         el.removeClass('emphasize-target');

         ev.preventDefault(); // We'll do the "default" with `pushState`.

         // Don't scroll past the bottom of the page or the animation will
         // abruptly get cut off.
         let targetY = Math.min(
            el.getPosition().y,
            document.body.clientHeight - window.innerHeight
         );

         // Don't animate at all if we're already at the right position
         // because that will delay the `onComplete` call unnecessarily.
         if (targetY == window.pageYOffset) {
            // This class can be used to add emphasis to something that was
            // already in view.
            el.addClass('emphasize-target');
            onComplete();
            return;
         }

         if (history.pushState) {
            // pushState won't make the browser scroll position jump, that way
            // we can animate it.
            history.pushState(null, null, hash);

            TweenMax.to(window, duration, {
               scrollTo: { y: targetY },
               onComplete: onComplete,
            });
         } else {
            // Fallback for older browsers.
            window.location.hash = hash;
            onComplete();
         }
      });
   },

   /**
    * Returns the zIndex of the first element containing this element (or of
    * this element itself) which has a zIndex that isn't 'auto'; if no element
    * in the stack has a zIndex other than 'auto', then returns 'auto'.
    */
   getEffectiveZindex: function () {
      let itr = this,
         zIndex;
      // eslint-disable-next-line no-empty
      while ((zIndex = itr.getStyle('zIndex')) == 'auto' && (itr = itr.getParent())) {}
      return zIndex;
   },

   shake: function () {
      let effect = new Fx.Morph(this, { duration: 50, transition: Fx.Transitions.linear });

      let pos = this.getStyle('position');
      this.setStyle('position', 'relative');

      let to = { left: [-10, 10] };
      let fro = { left: [10, -10] };

      effect.start({ left: [0, -10] }).chain(
         function () {
            effect.start(to);
         },
         function () {
            effect.start(fro);
         },
         function () {
            effect.start(to);
         },
         function () {
            effect.start(fro);
         },
         function () {
            effect.start(to);
         },
         function () {
            effect.start(fro);
         },
         function () {
            this.setStyles({
               left: 0,
               position: pos,
            });
         }.bind(this)
      );
   },

   addModalImage: function () {
      this.addEvent('click', function (ev) {
         ev.stop();
         let imgUrl = this.get('data-modalImg');
         Modal.openModalImg(imgUrl);
      });
   },

   truncateContent: function (options) {
      let size = this.getSize();
      let width = options.x ? options.x : size.x;
      let height = options.y ? options.y : size.y;
      let styles = options && options.styles ? options.styles : [];

      let text = this.get('text');
      let first = true;

      do {
         if (!first) {
            text = text.substring(0, text.lastIndexOf(' ')) + '...';
         }
         first = false;

         let el = new Element('div', {
            styles: {
               visibility: 'hidden',
               position: 'absolute',
               width: width,
               top: 0,
               left: 0,
            },
         })
            .set('text', text)
            .setStyles(styles)
            .inject($body);

         size = el.getSize();
         el.destroy();
      } while (size.y > height);

      this.set('text', text);
      return this;
   },

   /**
    * If the element is taller than the specified height, truncate it and place
    * a nice "Read More" link to toggle it on / off.
    */
   truncateByHeight: function (maxHeight) {
      // Use the wrapper to do the truncation so we can easily calculate the
      // full-height of element we're trying to truncate.
      let wrapper = new Element('div.truncated');

      let buffer = 30; // Ensure that amount truncated will never be *less*
      // than this distance. i.e. if it's 1px over
      // maxHeight, then we'll reduce maxHeight until
      // the element has at least buffer px hidden.
      let element = this;

      let showFullHeight = element.get('data-showall') == 'true';
      if (showFullHeight) {
         return;
      }

      // Temporarily use max-height during window load so the element never
      // exceeds it's allowed bounds.
      wrapper.setStyle('max-height', maxHeight + 'px');
      wrapper.wraps(element);

      // We won't know the full height of the element until the window loads
      // and only then can we determine if we really need the truncation
      onLoad(function () {
         let fullHeight = getFullHeight();
         // We may not need to truncate at all
         if (fullHeight < maxHeight) {
            return;
         }

         // If we're within 'buffer' pixels of maxHeight, reduce the maxHeight
         // so there is at least 'buffer' pixels to reveal with "Read More"
         if (fullHeight - buffer < maxHeight) {
            maxHeight = fullHeight - buffer;
         }

         // Replace max-height with height cause we now know the element's
         // full height is greater than maxHeight
         wrapper.setStyle('height', maxHeight + 'px');
         element.setStyle('max-height', '');
         wrapper.setStyle('max-height', '');

         addReadMoreLink();
      });

      function addReadMoreLink() {
         wrapper.grab(
            new Element('div', {
               class: 'gradient',
            })
         );

         let readMore = new Element('a.readMore', {
            html: _js('Read More') + ' <i class="fa fa-caret-down"></i>',
         });

         let readLess = new Element('a.readLess', {
            html: _js('Read Less') + ' <i class="fa fa-caret-up"></i>',
         });

         let buttonContainer = new Element('div', {
            class: 'readButtons',
         });

         buttonContainer.grab(readMore).grab(readLess);

         let growTween = new Fx.Tween(wrapper, {
            duration: 'short',
            property: 'height',
         });

         wrapper.adopt(buttonContainer);
         readMore.addEvent('click', toggleTruncation);
         readLess.addEvent('click', toggleTruncation);

         function toggleTruncation() {
            if (wrapper.hasClass('expanded')) {
               // from current to maxHeight
               growTween.start(maxHeight);
            } else {
               // from maxHeight to full height
               growTween.start(maxHeight, getFullHeight() + 30);
            }
            wrapper.toggleClass('expanded');
         }
      }

      /**
       * Gets the full height of the element including its top margin.
       * Since we wrap it, we need to know the eventual height of the wrapper
       * if it had no restrictions.
       */
      function getFullHeight() {
         let coords = element.getCoordinates(wrapper);
         // We need to include the top-margin of the element in its height
         // (since we're wrapping it).
         return coords.top + coords.height;
      }
   },

   isFixed: function () {
      let el = this;
      // Note that in the while condition/assignment we extend the offsetParent
      // manually with $ so that IE doesn't choke on getStyle and retrieve.
      do {
         if (
            el.getStyle('position') == 'fixed' ||
            el.retrieve('pinnedByJS') ||
            el.retrieve('fixedPosition')
         ) {
            return true;
         }
      } while ((el = $(el.offsetParent)));
      return false;
   },

   /**
    * Forces the element to retain it's current dimensions
    * even if the contents change.
    *
    * See: unpinDimensions()
    */
   pinDimensions: function () {
      let size = this.getComputedSize();
      let styles = {
         height: size.height,
         width: size.width,
         display: 'block',
      };
      this.setStyles(styles);
   },

   /**
    * Undoes the action of pinDimensions(), allowing an element to be sized
    * by CSS and it's contents.
    */
   unpinDimensions: function () {
      let styles = {
         height: '',
         width: '',
         display: '',
      };
      this.setStyles(styles);
   },
});

String.implement({
   // Turns text into safe html.
   encodeHtml: function () {
      return String(this)
         .replace(/&/g, '&amp;')
         .replace(/"/g, '&quot;')
         .replace(/'/g, '&#39;')
         .replace(/</g, '&lt;')
         .replace(/>/g, '&gt;');
   },

   rtrim: function (toTrim) {
      return this.replace(new RegExp(toTrim + '$', ''), '');
   },
});

// WINDOW EXTENSIONS //////////////////////////////////////////////////////////

/**
 * Adds a method to the window object that calculates the width of the scroll
 * bar (useful when adjusting the width of an element to account for the
 * presence of a scroll bar).
 */
window.getScrollbarWidth = function () {
   // Test the difference in clientWidth between no scroll and scroll.
   document.body.style.overflow = 'hidden';
   let width = document.body.clientWidth;
   document.body.style.overflow = 'scroll';
   width -= document.body.clientWidth;
   // Handle IE differently.
   if (!width) {
      width = document.body.offsetWidth - document.body.clientWidth;
   }
   // Reset overflow to the default.
   document.body.style.overflow = '';
   return width;
};

window.isFixed = function () {
   return false;
};

// ARRAY EXTENSIONS ///////////////////////////////////////////////////////////

/**
 * Extends Array with our own methods.
 */
Array.implement({
   /**
    * Returns the first item of the array, or undefined if it is empty.
    */
   first: function () {
      return this.length > 0 ? this[0] : undefined;
   },

   /**
    * Returns the last item of the array, or undefined if it is empty.
    */
   last: function () {
      return this.length > 0 ? this[this.length - 1] : undefined;
   },

   /**
    * returns the first element for which the given callback returns somethign
    * truthy.
    */
   find: function (callback) {
      let chosen = null;
      Array.some(this, function (item) {
         if (callback(item)) {
            chosen = item;
            return true;
         }
      });
      return chosen;
   },

   /**
    * Works like addEvent, but adds the event to each element of an array; adds
    * the two parameters to bind, allowing the caller to specify a context
    * and custom argument(s) with which to run the event handler. The custom
    * arguments must be an array if there's more than one, and they will be
    * appended to the first two arguments to the function: the element the
    * event is attached to and the event itself.
    */
   eachAddEvent: function (event, f, context, customArgs) {
      this.each(function (el) {
         el.addEvent(event, function (ev) {
            let args = [el, ev].concat(Array.convert(customArgs));
            context = context != undefined ? context : window;
            f.apply(context, args);
         });
      });
   },

   /**
    * Returns a copy of the array, sorted by a property of its elements.
    */
   sortByProperty: function (property) {
      return Array.convert(this).sort(function (left, right) {
         let a = left.getProperty(property);
         let b = right.getProperty(property);

         return a < b ? -1 : a > b ? 1 : 0;
      });
   },
});

// CUSTOM EVENTS //////////////////////////////////////////////////////////////

/**
 * A custom event that fires on any mousedown outside of the element the event
 * is attached to. Thanks to:
 *
 * http://appden.com/javascript/fun-with-custom-events-on-elements-in-mootools/
 */
Element.Events.clickoff = {
   base: 'click',
   condition: function (event) {
      // Stop event from bubbling up; note that it's important to use
      // stopPropagation() instead of stop() here, or clicking links inside the
      // element won't work.
      event.stopPropagation();
      return false;
   },
   onAdd: function (fn) {
      this.getDocument().addEvent('click', fn);
   },
   onRemove: function (fn) {
      this.getDocument().removeEvent('click', fn);
   },
};

// REQUEST EXTENSIONS /////////////////////////////////////////////////////////

if (window.Request == undefined) {
   window.Request = {};
}

Request.activeRequests = new Map();

Request.Batch = {
   options: {
      period: 1500,
   },

   hash: new Hash(),
   pinging: false,
   index: 0,

   add: function (req) {
      req.batchId = this.index++;
      this.hash.set(req.batchId, req);
      this.start();
   },

   remove: function (req) {
      if (!this.hash.has(req.batchId)) {
         return false;
      }

      this.hash.erase(req.batchId);
      if (!this.hash.getLength()) {
         this.stop();
      }

      return true;
   },

   ping: function () {
      this.pinging = true;

      let args = [];
      this.hash.each(function (req, key) {
         args.push([key, req.funcName, req.args]);
      });

      new Request.AjaxIO('Request.Batch', {
         onSuccess: function (result) {
            result.each(
               function (batch) {
                  let id = batch[0];
                  let response = JSON.parse(batch[1]);

                  if (response.AjaxIO.complete) {
                     this.hash.get(id).fireEvent('complete', [response.AjaxIOData]);
                     this.hash.erase(id);
                  }
               }.bind(this)
            );

            // Stop repeating if every request has been filled
            if (this.hash.getLength()) {
               this.nextPing = this.ping.delay(this._period, this);
               // exponential backoff.
               this._period *= 1.1;
            } else {
               this.pinging = false;
            }
         }.bind(this),
      }).send(args);
   },

   start: function () {
      // Always reset the period to the base value
      this._period = this.options.period;
      if (!this.pinging) {
         this.ping();
      }
   },

   stop: function () {
      this.pinging = false;
      clearInterval(this.nextPing);
   },
};

/**
 * new Request.AjaxIO(funcName, {
 *    // options...
 * }).send(arg1, arg2, ...);
 */
export default Request.AjaxIO = new Class({
   Implements: [Options, Events],

   options: {
      onSuccess: function () {},
      timeout: 0,
   },

   initialize: function (funcName, options) {
      this.setOptions(options);
      this.funcName = funcName;

      let self = this;
      let url = this.options.url ? this.options.url : window.location.toString().split('#')[0];

      // Safari is the only browser that returns window.location as a decoded string.
      // This is only a problem for % literals.

      // We don't check the Browser.Engine here because Chrome shares webkit, but doesn't
      // have this same problem. This regex should be safe to use everywhere.
      url = url.replace(/%(?![0-9a-fA-F]{2})/, '%25');

      this.request = new Request({
         url: url,
         onSuccess: this.onSuccess.bind(this),
         onComplete: function () {
            Request.activeRequests.delete(self);
            Request.completedRequests++;
            self.fireEvent('complete');
         },
         onCancel: function () {
            Request.activeRequests.delete(self);
            self.fireEvent('cancel');
            self._reject();
         },
         onFailure: function () {
            self.fireEvent('error');
            self._reject();
         },
      });
   },

   send: function () {
      let _this = this;
      let args = Array.convert(arguments);
      return new Promise(function (resolve, reject) {
         _this._resolve = resolve;
         _this._reject = reject;

         let post = {
            AjaxIO_function: _this.funcName,
            AjaxIO_args: JSON.stringify(args),
         };

         Request.activeRequests.set(_this, post);

         _this.request.send(Hash.toQueryString(post));
      });
   },

   ping: function () {
      this.args = Array.convert(arguments);
      Request.Batch.add(this);
      if (this.options.timeout > 0) {
         this.timeout.delay(this.options.timeout, this);
      }
   },

   timeout: function () {
      if (Request.Batch.remove(this)) {
         this.fireEvent('timeout');
      }
   },

   onSuccess: function (text) {
      let response = JSON.parse(text);
      if (!response.error) {
         if (response.AjaxIOData === undefined) {
            this._reject();
            if (window.console && console.error) {
               console.error('No data in response');
            }
         } else {
            this.fireEvent('onSuccess', [response.AjaxIOData]);
            this._resolve(response.AjaxIOData);
         }
      } else {
         this.fireEvent('onError', [response]);
         this._reject(response.error);
         if (window.console && console.error) {
            console.error(response.error);
         }
      }
   },
});
/**
 * A count of currently in-flight requests.
 *
 * Added so we can wait for ajax requests to finish in selenium tests by
 * waiting for this to be 0.
 */

Request.completedRequests = 0;

// For now lets enable this only for development for a while to make sure we
// don't run into any weird unintended behavior
if (!App.isProduction && !App.inFlightChecksDisabled) {
   window.addEventListener('beforeunload', function (e) {
      let activeAjaxRequest = function () {
         return (
            window.Request !== undefined &&
            window.Request.activeRequests &&
            window.Request.activeRequests.size > 0
         );
      };

      if (activeAjaxRequest()) {
         // Prompt the user asking them if they are sure they want to
         // leave due to unsaved changes.
         e.returnValue = '';

         // Fail during development to prevent flakiness/unexpected
         // behavior if requests are in flight while trying to leave the page.
         let activeAjaxRequests = JSON.stringify(
            Array.from(window.Request.activeRequests.values())
         );

         let errorMessage = `
            Active AJAX request(s) in flight:
            ${activeAjaxRequests}
         `;

         throw new Error(errorMessage);
      }
   });
}

// ASSET EXTENSIONS ///////////////////////////////////////////////////////////

// eslint-disable-next-line no-var
export var SettingsCookie = (window.SettingsCookie = (function () {
   return {
      get: function (name) {
         return Cookie.read(name);
      },
      set: function (key, value) {
         // ifixit-specific logic taken from CookieUtils::getCookieDomain.
         let baseDomain = window.location.host;
         if (App.which == 'ifixit') {
            baseDomain = parseDomain()['domain'];
         }
         let options = {
            domain: '.' + baseDomain,
            maxAgeInSeconds: 2592000, // 30 days
         };
         CookieUtils.set(key, value, options);
      },
   };
})());

export const getFlagIcon = countryCode => {
   return window.shared_constants.BaseURI('SITE_IMAGE_FLAG_64')(countryCode.toUpperCase());
};

/**
 * Works like a Group, but for a collection of Assets, which must have their
 * onload event handlers installed on instantiation and not after the fact.
 * Takes any number of arrays of hrefs (or plain href strings), creates Assets
 * for each href (according to the extension), and provides events for the
 * successful loading of all js, all css, and all files together.
 *
 * Events:
 *
 *    onCssComplete -- fired when all css files have loaded
 *    onJsComplete  -- fired when all js files have loaded
 *    onComplete    -- fired when all css and js files have loaded
 *
 * Warning:
 *
 *    You *must* provide an options object as the last argument to the
 *    constructor, which should have one or more of the events as attributes.
 *    Adding events to the group after the fact is risky, and could result in
 *    missing a load event.
 */
// eslint-disable-next-line no-var
export var AssetGroup = (window.AssetGroup = new Class({
   Implements: [Options, Events],

   options: {
      onCssComplete: function () {},
      onJsComplete: function () {},
      onComplete: function () {},
   },

   initialize: function () {
      this.cssCount = 0;
      this.cssLoaded = false;
      this.jsCount = 0;
      this.jsLoaded = false;

      let args = Array.convert(arguments);
      let options = args.pop();
      this.setOptions(options);

      let scripts = $$('script');
      let links = $$('link[rel="stylesheet"]');

      args.each(function (arg) {
         Array.convert(arg).each(function (file) {
            if (file.match(/\.js\b/)) {
               if (
                  !scripts.some(function (s) {
                     return s.src == file;
                  })
               ) {
                  this.jsCount += 1;
                  new Asset.javascript(file, {
                     onload: this.loaded.bind(this, 'js'),
                  });
               }
            } else if (file.match(/\.css\b/)) {
               if (
                  !links.some(function (l) {
                     return l.href == file;
                  })
               ) {
                  // https://mootools.lighthouseapp.com/projects/2706/tickets/186-asset-css-onload-event-does-not-fire-in-ff3-safari-opera
                  // Asset.css.onload does not fire for third-party domains, so disable it
                  this.cssCount += 1;
                  new Asset.css(file, {
                     onload: this.loaded.bind(this, 'css'),
                  });
                  //this.loaded.delay(100, this, 'css');
               }
            }
         }, this);
      }, this);

      // just in case we ended up not needing to add any new js or css files,
      // we check our lists of assets to see if firing events is in order
      this.fireEvents();
   },

   loaded: function (which) {
      this[which + 'Count'] -= 1;
      this.fireEvents();
   },

   // fires events in a very stateful manner, checking the queues of js and
   // css files to see if they're empty and if we haven't already fired off
   // events
   fireEvents: function () {
      if (this.cssCount <= 0 && !this.cssLoaded) {
         this.fireEvent('onCssComplete');
         this.cssLoaded = true;
      }
      if (this.jsCount <= 0 && !this.jsLoaded) {
         this.fireEvent('onJsComplete');
         this.jsLoaded = true;
      }
      if (this.cssCount <= 0 && this.jsCount <= 0) {
         this.fireEvent('onComplete');
         this.cssCount = this.jsCount = 0;
      }
   },
}));

// FORM CLASS /////////////////////////////////////////////////////////////////

// eslint-disable-next-line no-var
export var FormLibrary = (window.FormLibrary = {
   getFields: function (form) {
      return form.getElements('input,select,textarea,div[contenteditable]');
   },

   getEditableFields: function (form) {
      return this.getFields(form).filter(function (el) {
         return el.type != 'hidden' && !el.disabled;
      });
   },

   getValues: function (form, options) {
      form = $(form);
      if (!form || form.get('tag') != 'form') {
         return;
      }
      let indexRegex = /\[([a-zA-Z0-9#_-]*)\]/;
      let obj = {};

      options = Object.merge(
         {
            prefix: '',
            submitDisabled: false,
            ignoreClass: null,
         },
         options
      );

      Array.each(form.elements, function (el) {
         if (!el.name) {
            return;
         }
         if (el.name.substring(0, options.prefix.length) != options.prefix) {
            return;
         }
         if (el.type && (el.type == 'radio' || el.type == 'checkbox') && el.checked == false) {
            return;
         }
         if (el.disabled && el.disabled == true && !options.submitDisabled) {
            return;
         }

         let name = el.name;
         let indices = [];
         let groups = null;
         while ((groups = name.match(indexRegex))) {
            indices.push(groups[1]);
            name = name.replace(indexRegex, '');
         }
         indices.unshift(name);

         let pointer = obj;
         let index;
         let value;
         // eslint-disable-next-line no-var
         for (var i = 0; i < indices.length - 1; i++) {
            // eslint-disable-next-line no-var
            var next;
            index = indices[i];
            if (index === '') {
               next = indices[i + 1] === '' ? [] : {};
               pointer.push(next);
               pointer = next;
            } else {
               if (!pointer[index]) {
                  next = indices[i + 1] === '' ? [] : {};
                  pointer[index] = next;
               }
               pointer = pointer[index];
            }
         }

         // If the element has a class matching the "ignoreClass" class, then
         // the value there is just the default value, and we don't want to use
         // it.
         if (options.ignoreClass && el.hasClass(options.ignoreClass)) {
            value = '';
         } else {
            value = el.value;
         }

         index = indices[i];
         if (index === '') {
            pointer.push(value);
         } else {
            pointer[index] = value;
         }
      });
      return obj;
   },

   getFirstField: function (form) {
      let els = this.getEditableFields(form);
      let withIndices = els.filter(function (el) {
         let tabIndex = el.getProperty('tabIndex');
         // Items with no specified tabIndex now show a tabIndex of 0, not null.
         return tabIndex !== null && tabIndex >= 1;
      });
      let firstByIndex = withIndices.sortByProperty('tabIndex')[0];

      return firstByIndex ? firstByIndex : els[0];
   },

   focusFirst: function (form) {
      let first = this.getFirstField(form);
      first.focus();
   },

   selectedValue: function (el) {
      el = $(el);
      return el.options[el.selectedIndex].value;
   },

   submit: function (form) {
      form = $(form);

      if (typeof form.onsubmit == 'function' && !form.onsubmit()) {
         return false;
      } else {
         form.submit();
      }
   },

   create: function (obj) {
      let form = new Element('form');
      if (obj.id) {
         form.setProperty('id', obj.id);
      }

      if (obj['class']) {
         form.setProperty('class', obj['class']);
      }

      let formBody = new Element('div', { class: 'formBody' }).inject(form);
      Hash.each(obj.fields || {}, function (field, name) {
         let box = new Element('div', { class: 'formField' }).inject(formBody);
         let id = field.properties ? field.properties.id : false;
         if (field.label) {
            let label = new Element('label');
            label.setProperty('for', id || name);
            label.set('text', field.label);
            label.inject(box);
         }
         let el = null;
         if (field.type == 'text') {
            el = new Element('input', {
               name: name,
               type: 'text',
               value: field.value || '',
            }).setProperties(field.properties || {});
         }
         if (field.type == 'switch') {
            let input = new Element('input', {
               name: name,
               type: 'checkbox',
               value: field.value || false,
               class: 'switch-input',
            }).setProperties(field.properties || {});
            let span = new Element('span', {
               class: 'switch-track',
            });
            el = new Element('label', {
               class: 'switch',
            })
               .adopt(input)
               .adopt(span);
         }
         el.inject(box);
      });
      new Element('div', { class: 'formField' })
         .adopt(
            new Element('button', {
               type: 'submit',
               class: 'button button-action-solid',
               text: obj.submit || 'Submit',
            })
         )
         .inject(formBody);

      return form;
   },
});

export const Validation = (window.Validation = ValidationFunctions);

// DRAG.MOVE PATCHING /////////////////////////////////////////////////////////

/**
 * Modify Drag.Move to allow fixing movement to an axis by setting the modifier
 * for the axis to null.
 *
 * Example:
 *    new Drag.Move(el, {
 *       modifiers: {x: 'left', y: null}
 *    });
 *
 * This will restrict movement to the X-axis, and will furthermore register an
 * entry on a droppable only if the X-position is right, disregarding the
 * Y-position.
 */
if (Drag && Drag.Move) {
   Drag.Move.implement({
      checkAgainst: function (el, i) {
         let now = this.mouse.now;
         let entered = true;
         el = this.positions ? this.positions[i] : el.getCoordinates();
         const inX = now.x > el.left && now.x < el.right;
         const inY = now.y > el.top && now.y < el.bottom;
         return (
            (this.options.modifiers.y == null && inX) ||
            (this.options.modifiers.x == null && inY) ||
            (inX && inY)
         );
      },
   });
}

// UTILS CLASS ////////////////////////////////////////////////////////////////

// eslint-disable-next-line no-var
export var Utils = (window.Utils = (function () {
   return {
      log: function (body) {
         new Request.API_2_0('js_log/debug', {
            method: 'post',
            onSuccess: function (response) {
               return true;
            },
         }).send(body);

         return true;
      },

      viewportSize: function () {
         let w = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
         let h = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);

         return { width: w, height: h };
      },

      /**
       * An empty error handler function to be used as a default; simply swallows
       * application errors, but allows normal errors to propagate.
       */
      ignoreApplicationErrors: function (err) {
         if (!(err instanceof ApplicationError)) {
            throw err;
         }
      },

      /**
       * Similar to ignoreApplicationErrors, but designed to wrap a function which
       * expects a simple error string, so that that function doesn't have to deal
       * with distinguishing ApplicationErrors from normal ones.
       */
      filterApplicationErrors: function (f, err) {
         if (!(err instanceof ApplicationError)) {
            throw err;
         }
         f(err.message);
      },

      getUniqueId: function () {
         let id = 'el-' + Number.random(1, 1000);
         while ($(id) != undefined) {
            id += Number.random(1, 1000);
         }
         return id;
      },

      /**
       * Watches a window opened with window.open and executes the callback
       * when it's closed by the user
       */
      onChildWindowClose: function (win, callback, timeout) {
         // Default to timeout = 10 minutes
         timeout = timeout || 600000;
         // eslint-disable-next-line no-var
         var start = new Date().getTime(),
            windowChecker = setInterval(function () {
               if (win && win.closed) {
                  clearInterval(windowChecker);
                  callback(true);
               }

               if (new Date().getTime() - start > timeout) {
                  clearInterval(windowChecker);
                  callback(false);
               }
            }, 250);
      },

      /**
       * Utility for prompting the user to save any unsaved changes before
       * leaving the page.
       */
      UnsavedChanges: {
         /**
          * Add a handler that will be called when the user leaves the page. If
          * the handler returns a message, the browser will prompt the user.
          * Otherwise, the user will leave the page normally.
          */
         addHandler: function (handler) {
            if (!this.$handlers) {
               // Initialize the list of handlers and check them when the user
               // unloads the page.
               this.$handlers = [];
               onUnload(this.getMessage.bind(this));
            }

            this.$handlers.push(handler);
         },

         /**
          * Checks if any of the handlers currently have an unsaved changes
          * message for the user.
          */
         getMessage: function () {
            if (this.$handlers) {
               // Return the first truthy message returned.
               return Array.pick(
                  this.$handlers.map(function (handler) {
                     return handler();
                  })
               );
            }
         },
      },

      /**
       * Returns a truthy value if the browser would currently prompt the user
       * when attempting to leave the page.
       */
      hasUnsavedChanges: function () {
         // Check if there are any truthy messages.
         return !!Utils.UnsavedChanges.getMessage();
      },

      /**
       *
       * Helper function to create a DOM structure.
       * Pass it an object like the following:
       * config = {
       *    tag: 'span', // not required, defaults to 'div'
       *    c  : 'list_of css-classes', // not required
       *    // All other properties will be passed straight through to new Element(...)
       *    children : [array of DOM elements or config objects that will be passed to this function]
       * }
       *
       *
       *
       * Add a list of files in Utils/ *.js as properties on the Utils object
       * This allows us to reuse the scripts in isolated use cases liks embeds
       */
      createElements: function createElements(config) {
         if (typeof config === 'string') {
            return document.createTextNode(config);
         }

         // If this is already a DOM element, just return it;
         if (config.tagName) {
            return config;
         }

         let children = config.children,
            tag = config.tag,
            className = config.c;

         if (className) {
            config['class'] = className;
         }

         // Don't want these getting through to the underlying constructor.
         delete config.tag;
         delete config.children;
         delete config.c;

         let el = new Element(tag || 'div', config);

         if (children) {
            Array.each(children, function (child) {
               el.grab(createElements(child));
            });
         }

         return el;
      },

      /**
       * Returns an interface for throttling a function call
       * (similar to mootools` :pause Event pseudo)
       *
       * fire() can be called as many times as needed, but the
       * callback is called when fire() hasn't been called for `delay`
       * milliseconds.
       *
       * calling fireNow() executes callback() immediately and clears the timer.
       */
      throttler: function (delay, callback) {
         let timer;

         function fireNow() {
            clear();
            callback.apply(this, Array.convert(arguments));
         }

         function fireLater() {
            clear();
            timer = setTimeout(fireNow, delay);
         }

         function clear() {
            clearTimeout(timer);
         }

         return {
            fire: fireLater,
            fireNow: fireNow,
         };
      },

      /**
       * Returns an interface for throttling a function call.
       * The rate is limited to once per `delay` milliseconds.
       * The design ensures that calling `fire()` guarantees that `callback` will
       * be called within `delay` milliseconds.
       *
       * `fire()` can be called as many times as needed, but the
       * callback will only be called once per `delay` milliseconds.
       */
      rateLimiter: function (delay, callback) {
         let paused, shouldFire;

         function fire() {
            if (paused) {
               shouldFire = true;
               return;
            } else {
               callback.apply(this, Array.convert(arguments));
               pause();
            }
         }

         function pause() {
            shouldFire = false;
            clearTimeout(paused);
            paused = setTimeout(function () {
               paused = null;
               if (shouldFire) {
                  fire();
               }
            }, delay);
         }

         return fire;
      },

      /**
       * Creates an alert div and a close button.  Add this to any element to get
       * a nicely styled alert
       *
       * @contents: a string or an element to be placed in the alert.
       */
      createAlert: function (type, contents) {
         return this.createAlertPrompt(type, contents);
      },

      /**
       * Creates and returns an alert div with a prompt and 'Yes / No' links.
       * Both links close the alert and call `callback(yes)` and pass true if
       * Yes was clicked and false if No was clicked.
       *
       * @contents: a string or an element to be placed in the alert.
       */
      createAlertPrompt: function (type, contents, callback) {
         if (type) {
            type = '.status' + type.capitalize() + '.inPage';
         }

         let alertElement = new Element('div.status' + type);
         let yesnoElementType = 'a.yesno.xbuttonLink';
         let button = new Element('i.fa.fa-times.right.close-status', {
            events: {
               click: alertCloser(false),
            },
         });

         alertElement.set('data-text', contents);

         if (typeof contents === 'string') {
            alertElement.set('text', contents);
         } else {
            alertElement.grab(contents);
         }

         if (callback) {
            let YES = new Element(yesnoElementType, {
               text: _js('Yes'),
               events: {
                  click: alertCloser(true),
               },
            });
            let NO = new Element(yesnoElementType, {
               text: _js('No'),
               events: {
                  click: alertCloser(false),
               },
            });

            alertElement.grab(YES);
            alertElement.appendText('/');
            alertElement.grab(NO);
         }

         alertElement.grab(button, 'top');
         return alertElement;

         function alertCloser(confirmed) {
            return function (ev) {
               if (ev) {
                  ev.stop();
               }
               alertElement.dispose();
               if (callback) {
                  callback(confirmed);
               }
            };
         }
      },

      /**
       * Adds an event handler to "From" that fires the same event with the same
       * arguemtns on "To"
       */
      passEvent: function (eventName, from, to) {
         from.addEvent(eventName, function () {
            to.fireEvent(eventName, arguments);
         });
      },

      /**
       * Provides a collection of methods to implement Events.
       *
       * Use it like this to add events to a class:
       *    Object.append(Thing.prototype, Utils.EventsFunctions);
       *
       * ======  Warning!!! ======
       * doing   Object.append(Thing.prototype, Events.prototype)
       *    or   Object.append(Thing.prototype, new Events())
       *
       * Causes a dangerous bug.  Events.prototype contains the '$events'
       * property, so every new instance of it contains the '$events' property.
       * This is a problem when using one of these objects as a prototype,
       * this.$events will resolve to the same Thing.prototype.$events property
       * every time.  This means that every instance shares the same collection
       * of event handlers, which is obviously bad.
       *
       * The solution is to re-implement the individual event functions and
       * auto-initialize this.$events if it's missing.
       *
       * These functions were copied from Mootools 1.4.5 then the
       * "if (this.$events) ..." was inserted in each method.
       */
      EventsFunctions: {
         addEvent: function (type, fn, internal) {
            type = removeOn(type);
            if (!this.$events) {
               this.$events = {};
            }

            /*<1.2compat>*/
            if (fn == $empty) {
               return this;
            }
            /*</1.2compat>*/

            this.$events[type] = (this.$events[type] || []).include(fn);
            if (internal) {
               fn.internal = true;
            }
            return this;
         },

         addEvents: function (events) {
            if (!this.$events) {
               this.$events = {};
            }

            for (let type in events) {
               this.addEvent(type, events[type]);
            }
            return this;
         },

         fireEvent: function (type, args, delay) {
            if (!this.$events) {
               return this;
            }

            type = removeOn(type);
            let events = this.$events[type];
            if (!events) {
               return this;
            }
            args = Array.convert(args);
            events.each(function (fn) {
               if (delay) {
                  fn.delay(delay, this, args);
               } else {
                  fn.apply(this, args);
               }
            }, this);
            return this;
         },

         removeEvent: function (type, fn) {
            if (!this.$events) {
               return this;
            }

            type = removeOn(type);
            let events = this.$events[type];
            if (events && !fn.internal) {
               let index = events.indexOf(fn);
               if (index != -1) {
                  delete events[index];
               }
            }
            return this;
         },

         removeEvents: function (events) {
            if (!this.$events) {
               return this;
            }

            let type;
            if (typeOf(events) == 'object') {
               for (type in events) {
                  this.removeEvent(type, events[type]);
               }
               return this;
            }
            if (events) {
               events = removeOn(events);
            }
            for (type in this.$events) {
               if (events && events != type) {
                  continue;
               }
               let fns = this.$events[type];
               for (let i = fns.length; i--; ) {
                  if (i in fns) {
                     this.removeEvent(type, fns[i]);
                  }
               }
            }
            return this;
         },
      },

      cookieDuration: function (seconds) {
         let secondInDays = 0.000011574; // 1 seconds => 1 / (24 * 60 * 60)
         return secondInDays * seconds;
      },

      // Returns a promise that resolves after `duration` ms.
      timeoutPromise: function (duration) {
         return new Promise(function (resolve, reject) {
            setTimeout(resolve, duration);
         });
      },
   };

   /**
    * Both removeOn and $empty are only used for the above Utils.EventsFunctions
    */
   function removeOn(string) {
      return string.replace(/^on([A-Z])/, function (full, first) {
         return first.toLowerCase();
      });
   }
   // eslint-disable-next-line no-var
   function $empty() {}
})());

/*
HTMLEncode - Encode HTML special characters.
Copyright (c) 2006 Thomas Peri, http://www.tumuski.com/

Permission is hereby granted, free of charge, to any person obtaining a
copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The Software shall be used for Good, not Evil.

The above copyright notice and this permission notice shall be included
in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

/**
 * HTML-Encode the supplied input
 *
 * Parameters:
 *
 * (String)  source    The text to be encoded.
 *
 * (boolean) display   The output is intended for display.
 *
 *                     If true:
 *                     * Tabs will be expanded to the number of spaces
 *                       indicated by the 'tabs' argument.
 *                     * Line breaks will be converted to <br />.
 *
 *                     If false:
 *                     * Tabs and linebreaks get turned into &#____;
 *                       entities just like all other control characters.
 *
 * (integer) tabs      The number of spaces to expand tabs to.  (Ignored
 *                     when the 'display' parameter evaluates to false.)
 *
 * v 0.3 - January 4, 2006
 */
export { htmlEncode };
window.htmlEncode = htmlEncode;
function htmlEncode(source, display, tabs) {
   function special(source) {
      let result = '';
      for (let i = 0; i < source.length; i++) {
         let c = source.charAt(i);
         if (c < ' ' || c > '~') {
            c = '&#' + c.charCodeAt() + ';';
         }
         result += c;
      }
      return result;
   }

   function format(source) {
      // Use only integer part of tabs, and default to 4
      tabs = tabs >= 0 ? Math.floor(tabs) : 4;

      // split along line breaks
      let lines = source.split(/\r\n|\r|\n/);

      // expand tabs
      for (let i = 0; i < lines.length; i++) {
         let line = lines[i];
         let newLine = '';
         for (let p = 0; p < line.length; p++) {
            let c = line.charAt(p);
            if (c === '\t') {
               let spaces = tabs - (newLine.length % tabs);
               for (let s = 0; s < spaces; s++) {
                  newLine += ' ';
               }
            } else {
               newLine += c;
            }
         }
         // If a line starts or ends with a space, it evaporates in html
         // unless it's an nbsp.
         newLine = newLine.replace(/(^ )|( $)/g, '&nbsp;');
         lines[i] = newLine;
      }

      // re-join lines
      let result = lines.join('<br />');

      // break up contiguous blocks of spaces with non-breaking spaces
      result = result.replace(/ {2}/g, ' &nbsp;');

      // tada!
      return result;
   }

   let result = source;

   // ampersands (&)
   result = result.replace(/\&/g, '&amp;');

   // less-thans (<)
   result = result.replace(/\</g, '&lt;');

   // greater-thans (>)
   result = result.replace(/\>/g, '&gt;');

   if (display) {
      // format for display
      result = format(result);
   } else {
      // Replace quotes if it isn't for display,
      // since it's probably going in an html attribute.
      result = result.replace(new RegExp('"', 'g'), '&quot;');
   }

   // special characters
   result = special(result);

   // tada!
   return result;
}

/**
 * NOTE: Similar code used in Cart/Product page. This is a sorta
 * generalized function. It can be used elsewhere but will probably have to be
 * modified.
 *
 * TODO: Finalize code when both code paths are stable -4/11/2011
 */
// eslint-disable-next-line no-var
export var FloatingSidebar = (window.FloatingSidebar = new Class({
   Implements: Options,

   options: {
      topSpacing: 16,
      topLimit: 0,
      bottomLimit: null,
      // #CartAdd (Cart specific switch)
      onCart: false,
      sections: [],
      scrollFx: {
         duration: 300,
         transition: 'quad:out',
      },
      sectionFx: {
         duration: 300,
         link: 'cancel',
         transition: 'quad:in:out',
      },
   },

   sidebar: null,
   marginTop: null,

   topLimit: null,
   bottomLimit: null,

   sections: [],
   scroll: null,
   changing: false,

   // #CartAdd (Don't jump if scroll changed active index)
   sidebarJump: false,
   scrolling: false,

   initialize: function (sidebarEl, options) {
      this.setOptions(options);
      this.sidebar = sidebarEl;
      this.marginTop = parseInt(this.sidebar.getStyle('margin-top'), 10);
      this.startXPos = window.getScroll().x;

      this.updateLimits();

      this.scroll = new Fx.Scroll(document, this.options.scrollFx);

      // #CartAdd
      this.scroll.addEvent(
         'start',
         function (ev) {
            this.scrolling = true;
         }.bind(this)
      );

      this.scroll.addEvent(
         'chainComplete',
         function (ev) {
            this.scrolling = false;
         }.bind(this)
      );

      this.sections = this.options.sections;
      this.sections.each(function (section) {
         if (section.hasClass('hidden')) {
            section.hide().removeClass('hidden');
         }
         section.set('reveal', this.options.sectionFx);
      }, this);

      this.sidebar.getElements('li a').each(function (a) {
         a.addEvent(
            'click',
            function (ev) {
               ev.stop();
               this.jumpFromSidebarLink(a);
            }.bind(this)
         );
      }, this);

      // Cart add
      this.sidebar.getElements('li').each(function (li) {
         li.addEvent(
            'click',
            function (ev) {
               ev.stop();
               this.jumpFromSidebarLink(li.getElement('a'));
            }.bind(this)
         );
      }, this);

      let learnMoreLinks = document.getElements('.content a[href$=learnMore]');
      learnMoreLinks.each(function (a) {
         a.addEvent(
            'click',
            function (ev) {
               ev.stop();
               let link = this.sidebar.getElement('a[href$=learnMore]');
               this.jumpFromSidebarLink(link);
            }.bind(this)
         );
      }, this);

      let advanceLinks = document.getElements('.advanceSection');
      advanceLinks.each(function (a) {
         a.addEvent(
            'click',
            function (ev) {
               ev.stop();
               this.advanceSection(a.getParent('.section'));
            }.bind(this)
         );
      }, this);

      this.updateSidebar = this.updateSidebar.bind(this);
      window.addEvent('scroll', this.updateSidebar);
      this.updateSidebar();

      // If there's a hash set and it's valid, jump to it.
      let hash = window.location.hash,
         anchor;
      if (hash != '' && (anchor = document.getElement('a' + hash))) {
         let toSection = anchor.getParent('div.section');
         let fromSection = this.getOpenSection();
         this.changeSection(fromSection, toSection);
      }
   },

   advanceSection: function (currSection) {
      let currIndex = this.sections.indexOf(currSection);
      let nextIndex = (currIndex + 1) % this.sections.length;
      let nextSection = this.sections[nextIndex];
      this.changeSection(currSection, nextSection);
   },

   jumpFromSidebarLink: function (a) {
      let currSection = this.getOpenSection();
      let selector = 'a#' + a.get('href').substr(1);
      let newSection = document.getElement(selector).getParent('.section');
      // #CartAdd
      this.updateHash(newSection);
      this.sidebarJump = true;
      this.changeSection(currSection, newSection);
      this.sidebarJump = false;
   },

   changeSection: function (currSection, toSection) {
      if (this.changing || this.scrolling || toSection == null || currSection == toSection) {
         return;
      }

      let currActiveEl = this.sidebar.getElement('li.active');
      currActiveEl.removeClass('active');

      let id = toSection.getElement('a.sectionAnchor').get('id');
      let menuItem = this.sidebar.getElement('a[href$=' + id + ']');
      let toActiveEl = menuItem.getParent('li');
      toActiveEl.addClass('active');

      // #CartAdd
      let scrollDuration;
      if (this.options.onCart) {
         scrollDuration = Math.ceil(this.options.scrollFx.duration);
         // 0 scroll duration when the scroll bar is at the top, and max duration
         // when it's at the bottom.
      } else {
         scrollDuration = Math.ceil(
            this.options.scrollFx.duration *
               (window.getScroll().y / (window.getScrollSize().y - window.getSize().y))
         );
         // Update the hash while the new section is still set to
         // display:none, so that the page doesn't jump around outside the
         // animation.
         this.updateHash(toSection);
      }

      this.scroll.setOptions({ duration: scrollDuration });

      currSection.removeClass('openSection');
      toSection.addClass('openSection');

      let currIndex = this.sections.indexOf(currSection);
      let toIndex = this.sections.indexOf(toSection);
      let inFx, outFx;
      this.changing = true;
      // #CartAdd: (Cart just jumps to anchor)
      if (this.options.onCart) {
         /**
          * NOTE: This jump tries to line the active menu item with the
          * section header.
          */
         if (this.sidebarJump) {
            let sideElTop = toActiveEl.getCoordinates().top;
            let scrollOffset = $('productPageNav').getCoordinates().top;
            let sideElPointHeight = toActiveEl.getComputedSize().totalHeight / 2;

            let offset = sideElTop - scrollOffset - sideElPointHeight;

            let jump = 0;
            if (!toSection.hasClass('topSection')) {
               jump = toSection.getElement('h1.sectionTitle').getCoordinates().top - offset;
            }
            this.scroll.start(0, jump);
         }
         this.changing = false;
      } else {
         if (currIndex < toIndex) {
            // New content from below.
            this.scroll.toTop().chain(
               function () {
                  toSection.show();
                  outFx = currSection.get('reveal');
                  outFx.dissolve().chain(
                     function () {
                        this.updateLimits();
                        this.changing = false;
                     }.bind(this)
                  );
               }.bind(this)
            );
         } else {
            // New content from above.
            this.scroll.toTop().chain(
               function () {
                  inFx = toSection.get('reveal');
                  inFx.reveal().chain(
                     function () {
                        currSection.hide();
                        this.updateLimits();
                        this.changing = false;
                     }.bind(this)
                  );
               }.bind(this)
            );
         }
      }
   },

   updateSidebar: function () {
      let scroll = window.getScroll().y;
      this.updateLimits();

      // Check for x offset
      let xOffset = window.getScroll().x - this.startXPos;
      // TODO: Use parent css to determine actual margin instead of hardcode
      if (xOffset < this.startXPos) {
         xOffset = this.startXPos;
      }

      if (this.sidebar.getStyle('margin-left') != -xOffset) {
         this.sidebar.setStyle('margin-left', -xOffset);
      }

      // Update the margin on the sidebar so that it stays near the top of the
      // window as you scroll down, but doesn't overlap anything on the bottom.
      let newMargin = Math.max(this.marginTop - scroll, this.topLimit);
      this.sidebar.setStyle('margin-top', newMargin);

      if (this.bottomLimit !== null) {
         let bottom = this.sidebar.getCoordinates().bottom;
         if (bottom > this.bottomLimit) {
            newMargin -= bottom - this.bottomLimit;
            this.sidebar.setStyle('margin-top', newMargin);
         }
      }

      // #CartAdd (Cart updates active index)
      if (!this.scrolling && this.options.onCart) {
         let currSection = this.getOpenSection();
         let activeSection = currSection;

         this.sections.each(function (section) {
            let indexSectionTop = section.getCoordinates().top;
            let id = section.getElement('a.sectionAnchor').get('id');
            let menuItem = this.sidebar.getElement('a[href$=' + id + ']');
            let bodySectionBottom = menuItem.getCoordinates().top;
            if (indexSectionTop < bodySectionBottom) {
               activeSection = section;
            }
         }, this);

         this.changeSection(currSection, activeSection);
      }
   },

   updateLimits: function () {
      let topLimit = this.options.topLimit;
      let bottomLimit = this.options.bottomLimit;

      if (typeof topLimit == 'function') {
         topLimit = topLimit();
      }
      if (typeof bottomLimit == 'function') {
         bottomLimit = bottomLimit();
      }

      this.topLimit = topLimit + this.options.topSpacing;

      if (bottomLimit !== null) {
         this.bottomLimit = bottomLimit;
      } else {
         this.bottomLimit = window.getScrollSize().y;
      }
   },

   updateHash: function (section) {
      // This is extra complicated because some browsers scroll the page
      // when setting the hash to an id corresponding to an element that is
      // display:none. The idea is to temporarily insert an element with the
      // matching id at the top of the scroll in order to avoid scrolling.
      // See http://stackoverflow.com/questions/1489624
      let a = section.getElement('.sectionAnchor'),
         tmp;
      let id = a.get('id');
      a.set('id', '');
      tmp = new Element('div', {
         id: id,
         styles: {
            position: 'absolute',
            visibility: 'hidden',
            top: window.getScroll().y,
         },
      });
      document.location.hash = '#' + id;
      tmp.dispose();
      a.set('id', id);
   },

   getOpenSection: function () {
      return document.getElement('.openSection');
   },
}));

// eslint-disable-next-line no-var
export var PaymentValidation = (window.PaymentValidation = {
   getCardType: function (numberIn) {
      const number = String(numberIn);
      let type = '';

      if (number.match(/^4/) != null) {
         type = 'VISA';
      } else if (number.match(/^(34|37)/) != null) {
         type = 'AMEX';
      } else if (number.match(/^5[1-5]/) != null) {
         type = 'MC';
      } else if (number.match(/^6011/) != null) {
         type = 'NOVA'; // Discover
      }

      return type;
   },

   /*
    * This function cheks the checksum of CreditCard
    * @param cc - the string with credit card number
    * @param accepted - the array of allowed CC types
    */
   isValidCreditCard: function (cc) {
      cc = String(cc);

      if (cc.length < 4 || cc.length > 30) {
         return false;
      }

      // Start the Mod10 checksum process...
      let checksum = 0;
      let location;
      let digit;

      // Add even digits in even length strings or odd digits in odd length strings.
      for (location = 1 - (cc.length % 2); location < cc.length; location += 2) {
         digit = parseInt(cc.substring(location, location + 1), 10);

         if (isNaN(digit)) {
            return false;
         }
         checksum += digit;
      }

      // Analyze odd digits in even length strings
      // or even digits in odd length strings.
      for (location = cc.length % 2; location < cc.length; location += 2) {
         digit = parseInt(cc.substring(location, location + 1), 10);

         if (isNaN(digit)) {
            return false;
         }
         if (digit < 5) {
            checksum += digit * 2;
         } else {
            checksum += digit * 2 - 9;
         }
      }

      if (checksum % 10 != 0) {
         return false;
      }

      return PaymentValidation.getCardType(cc) != '';
   },
});

// eslint-disable-next-line no-var
export var Geo = (window.Geo = {
   // This list should match the `getEuCountriesList` list in GeoUtils.php.
   EUCountries: [
      'AT',
      'BE',
      'BG',
      'CY',
      'CZ',
      'DE',
      'DK',
      'EE',
      'ES',
      'FI',
      'FR',
      'GB',
      'GR',
      'HR',
      'HU',
      'IE',
      'IT',
      'LT',
      'LU',
      'LV',
      'MT',
      'NL',
      'PL',
      'PT',
      'RO',
      'SE',
      'SI',
      'SK',
   ],

   /**
    * Calls the provided callback with the user's country code. Retrieves the
    * country code from a Cookie if it exists. If not, it is retrieved from the
    * API and set in a Cookie.
    */
   locate: function (callback) {
      // function to call the callback with the promise's result
      function resultToCallback(result) {
         if (callback) {
            callback(result.cc, result.rc);
         }
         return result;
      }

      if (Geo._location) {
         return Geo._location.then(resultToCallback);
      }

      Geo._location = new Promise(function (resolve) {
         function done(countryCode, regionCode) {
            resolve({ cc: countryCode, rc: regionCode });
         }

         // Allow easy and simple override for debugging.
         let countryMatch = window.location.search.match(/countryCode=(\w+)/);
         let regionMatch = window.location.search.match(/regionCode=(\w+)/);

         if (countryMatch || regionMatch) {
            if (countryMatch && regionMatch) {
               return done(countryMatch[1], regionMatch[1]);
            } else if (countryMatch) {
               return done(countryMatch[1]);
            } else {
               return done(undefined, regionMatch[1]);
            }
         }

         let countryCode = Cookie.read('countryCode');
         let regionCode = Cookie.read('regionCode');

         if (countryCode !== null && regionCode !== null) {
            return done(countryCode, regionCode);
         } else {
            new Request.API_2_0('geo_locate', {
               method: 'get',
               onSuccess: function (response) {
                  countryCode = response.country_code;
                  regionCode = response.region_code;

                  CookieUtils.set('countryCode', countryCode, {
                     maxAgeInSeconds: 432000, // 5 days
                  });
                  CookieUtils.set('regionCode', regionCode, {
                     maxAgeInSeconds: 432000, // 5 days
                  });

                  done(countryCode, regionCode);
               },
               onError: done,
            }).send();
         }
      });

      return Geo._location.then(resultToCallback);
   },

   runEverywhereButEU: function (callback) {
      Geo.locate(function (countryCode, regionCode) {
         if (countryCode) {
            let cCode = countryCode.toUpperCase();
            let isEUCountry = Geo.EUCountries.indexOf(cCode) >= 0;

            if (!isEUCountry) {
               callback();
            }
         }
      });
   },
});

// Our tiny promise library
/**
 * ========================
 * NOTE: DEPRECATED! Please use the standards compliant Promise object.
 * ========================
 *
 * A dead simple promise / futures implementation
 *
 * Caller
 * ======
 *
 * var data = getStuffFromServer();
 * data.getValue(function ready(response) {
 *    // do something with response
 * });
 *
 * function getStuffFromServer() {
 *    var data = new Future();
 *    new Request({
 *       ...
 *       onComplete: function(response) {
 *          data.resolve(response);
 *       }
 *    });
 *    return data;
 * }
 *
 */
export { Future };
window.Future = Future;
function Future() {
   this.callbacks = [];
   this.resolved = false;
}

(function (prototype) {
   /**
    * Resolve this promise with the specified value
    *
    * Effectively "set" the value that will be passed to the getValue callbacks
    */
   prototype.resolve = function (resolution) {
      let self = this;
      if (self.resolved) {
         throw "Can't resolve a promise more than once";
      }

      self.resolution = resolution;
      self.resolved = true;

      self.callbacks.each(function (callback) {
         callback(resolution, self._error);
      });
      self.callbacks = [];
   };

   /**
    * Resolve this promise with an error.
    *
    * `error` will be passed to each callback like so:
    * callback(value, error)
    */
   prototype.error = function (error) {
      this._error = error;
      this.resolve();
   };

   /**
    * Get the value of this promise when it's ready. The resolved value will
    * be passed to the readyCallback().
    *
    * readyCallback: function(value, error) {}
    *
    * Returns: true if this promise is already resolved and readyCallback
    * was called immediately.
    *
    * Note: error is normally null and is of course, not required
    */
   prototype.getValue = function (readyCallback) {
      if (this.resolved) {
         readyCallback(this.resolution, this._error);
         return true;
      } else {
         this.callbacks.push(readyCallback);
         return false;
      }
   };

   /**
    * Returns true if the promise has been resolved.
    */
   prototype.isResolved = function () {
      return this.resolved;
   };
})(Future.prototype);

/**
 * Add the 'beforePrint' and 'afterPrint' events to window. Note: these may
 * fire multiple times (including before/after) while actually printing.
 *
 * Use them like so:
 * window.addEvent('beforePrint', function() {
 *    ...
 * });
 *
 * Derived from the code seen here:
 * http://tjvantoll.com/2012/06/15/detecting-print-requests-with-javascript/
 */
(function () {
   let inPrintMode = false;
   function beforePrint() {
      // Prevent multiple firings
      if (inPrintMode) {
         return;
      }
      window.fireEvent('beforePrint');
   }
   function afterPrint() {
      inPrintMode = false;
      window.fireEvent('afterPrint');
   }

   if (window.matchMedia) {
      let mediaQueryList = window.matchMedia('print');
      if (mediaQueryList.matches) {
         beforePrint();
      }
      mediaQueryList.addListener(function (mql) {
         if (mql.matches) {
            beforePrint();
         } else {
            afterPrint();
         }
      });
   }

   window.onbeforeprint = beforePrint;
   window.onafterprint = afterPrint;
})();

(function () {
   /**
    * Hack: Work around a bug in mootools that will append "#undefined" to
    * the page url after using SmoothScroll.
    *
    * Proxy the constructor to default `anchor` to '' instead of undefined.
    */
   let SmoothScroll = Fx.SmoothScroll;
   Fx.SmoothScroll = function () {
      this.anchor = '';
      SmoothScroll.apply(this, arguments);
   };
   Fx.SmoothScroll.prototype = SmoothScroll.prototype;
})();
